import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FileText, Users, Rocket } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    number: '01',
    title: 'Post your need',
    description:
      'Define goals, timeline, and budget. Our matching engine finds the best-fit CXOs.',
    icon: FileText,
  },
  {
    number: '02',
    title: 'Meet & propose',
    description:
      'Review profiles, shortlist, and run discovery calls. Receive structured proposals in 48 hours.',
    icon: Users,
  },
  {
    number: '03',
    title: 'Engage & track',
    description:
      'Sign, kick off, and manage delivery with milestones, reports, and clear outcomes.',
    icon: Rocket,
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const lineRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger animation
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.fromTo(
            card,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              delay: index * 0.15,
              scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });

      // Connector line animation
      if (lineRef.current) {
        gsap.fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: lineRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="section-flowing bg-navy relative z-20"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            How It Works
          </span>
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white max-w-3xl mx-auto">
            From brief to executive impact—in days, not months.
          </h2>
        </div>

        {/* Steps Grid */}
        <div className="relative">
          {/* Connector Line (Desktop) */}
          <div
            ref={lineRef}
            className="hidden lg:block absolute top-24 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent origin-left"
            style={{ transform: 'scaleX(0)' }}
          />

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div
                key={step.number}
                ref={(el) => { cardsRef.current[index] = el; }}
                className="relative opacity-0"
              >
                <div className="surface-panel p-8 h-full hover:border-teal/30 transition-colors duration-300">
                  {/* Step Number */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-5xl font-bold text-teal/20">
                      {step.number}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-teal" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-xl font-semibold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
